/**
 * Angular-free readers for a form-schema's declared numeric bounds.
 *
 * Kept deliberately free of any `@angular` import so it can be shared by BOTH
 * the mounted sheet components (via `schema-validators.util.ts`, which adds the
 * `ValidatorFn` layer) AND the DI-free pure validators under
 * `services/mbrs/validators/` (which must not import Angular — enforced by
 * `npm run check:validator-purity`). One source of truth for "what is this
 * control's min?" across the headless and mounted paths, so the two cannot
 * drift on negativity rules.
 *
 * See `schema-validators.util.ts` for the rationale and the cross-sheet
 * spelling history.
 */

/** A single `validators[]` entry as it appears in `role-*.form.json`. */
export interface SchemaValidatorDef {
  name?: string;
  /** Bound spelling used by roles 10/11/23 (`{ name: 'min', value: 0 }`). */
  value?: number;
  /** Bound spelling used by role 6 (`{ name: 'min', min: 0 }`). */
  min?: number;
  /** Bound spelling for an explicit max (`{ name: 'max', max: N }`). */
  max?: number;
  /** Regex source for a `{ name: 'pattern', pattern: '…' }` entry. */
  pattern?: string;
}

/**
 * Minimal shape of a schema control these readers need. `ui` is intentionally
 * loose (`any`): each per-sheet component declares its own closed `ui`
 * interface (FormControlUI, etc.) that carries many other fields and does not
 * list `positiveWholeNumber`, so a stricter shape here trips either TS's
 * weak-type check or its "index signature missing" rule. The only `ui` field
 * read is `positiveWholeNumber`, guarded by optional chaining.
 */
export interface SchemaControlLike {
  type?: string;
  validators?: SchemaValidatorDef[] | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ui?: any;
}

/**
 * XBRL control types whose semantics are inherently non-negative even without
 * an explicit `min` validator entry (role 6's ssmt:NumberOfEmployees is the only control relying on this).
 */
const NON_NEGATIVE_TYPES = new Set<string>([
  'NonNegativeIntegerItemType',
  'NonNegativeInteger',
]);

/** Read a numeric bound off a validator entry, tolerating either spelling. */
function readBound(def: SchemaValidatorDef | undefined, fallbackKey: 'min' | 'max'): number | undefined {
  if (!def)
    return undefined;

  if (typeof def.value === 'number')
    return def.value;

  if (typeof def[fallbackKey] === 'number')
    return def[fallbackKey];

  return undefined;
}

/**
 * Resolve the declared non-negative lower bound for a single control, or
 * `undefined` when the control carries no min constraint.
 *
 * Three sources, in order of precedence:
 *  1. an explicit `{ name: 'min' }` validator entry (bound read from `value`
 *     or `min`);
 *  2. a non-negative XBRL control type (`NonNegativeInteger…`) ⇒ `0`;
 *  3. `ui.positiveWholeNumber: true` ⇒ `0` (whole-number share counts).
 */
export function getSchemaMin(control: SchemaControlLike | null | undefined): number | undefined {
  const minDef = control?.validators?.find((validator) => validator?.name === 'min');
  const explicit = readBound(minDef, 'min');
  if (explicit !== undefined)
    return explicit;

  if (control?.type && NON_NEGATIVE_TYPES.has(control.type))
    return 0;

  if (control?.ui?.positiveWholeNumber)
    return 0;

  return undefined;
}

/** Read the declared max bound for a control, or `undefined`. */
export function getSchemaMax(control: SchemaControlLike | null | undefined): number | undefined {
  const maxDef = control?.validators?.find((validator) => validator?.name === 'max');
  return readBound(maxDef, 'max');
}

/**
 * Whether a present value falls below the control's declared min. Empty values
 * (`null` / `undefined` / `''`) are NOT violations — emptiness is a required
 * check's concern, not a min check's. Controls with no declared min never
 * violate.
 */
export function valueBelowSchemaMin(control: SchemaControlLike | null | undefined, value: unknown): boolean {
  const min = getSchemaMin(control);
  if (min === undefined)
    return false;

  if (value === null || value === undefined || value === '')
    return false;

  const num = typeof value === 'number' ? value : Number(value);
  return !Number.isNaN(num) && num < min;
}
